import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogType } from '../medi-dialog/medi-dialog.component';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogPresenter } from '../medi-dialog/DialogPresenter';
import { OroService } from '../oroservice';
import { PostalCodeModule } from '../postal-code/postal-code.module';
import { CaseService } from './case.service';

export interface DialogData {
  dossierinfo: any;
  refreshDossiers?: () => void;
}

type DossierItem = {
    Key: string;
    Value: string;
}

@Component({
    selector: 'CaseDetail',
    templateUrl: './case-detail.html',
    styleUrls: ['./case-detail.scss'],
    standalone: true,
    imports: [
        DatePipe,
        DecimalPipe,
        FormsModule,
        TranslateModule,
        PostalCodeModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatDialogModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatTableModule,
        MatTooltipModule,
    ]
})
export class CaseDetail implements OnInit {
  public readonly oroservice = inject(OroService);
  public readonly data: DialogData = inject(MAT_DIALOG_DATA);
  public readonly translate = inject(TranslateService);
  private readonly caseService = inject(CaseService);
  private readonly dialog = inject(DialogPresenter);

    @ViewChild('scrollContent') private scrollContent: ElementRef;

    dossierDisplayedColumns: string[] = ['Key', 'Value'];
    dossierDatasource: DossierItem[];

    isUpdating: boolean = false;

    showdirektzahlung = false;
    showstundung = false;
    showadresseRechnungsempfaenger = false;
    showrechnungzurueckziehen = false;
    showdossierabschliessen = false;
    showreclamation = false;

    showdirektzahlungBtn = true;
    showstundungBtn = true;
    showadresseRechnungsempfaengerBtn = true;
    showrechnungzurueckziehenBtn = true;
    showdossierabschliessenBtn = true;

    betreibungsrueckzug = false;

    reclamationBody = "";
    isSendingReclamation = false;

    monate : number = 0;
    public naechstesDatum: string;

    direktzahlung: any = {};

    ngOnInit(): void {
        if (this.data.dossierinfo != null) {
            this.dossierDatasource = [];
            this.dossierDatasource.push({ Key: 'Dossieransicht.DossierId', Value: this.data.dossierinfo.DossierNr });
            this.dossierDatasource.push({ Key: 'Dossieransicht.Rechnungsempfaenger', Value: this.data.dossierinfo.Rechnungsempfaenger });
            this.dossierDatasource.push({ Key: 'Dossieransicht.Rechnungsnummer', Value: this.data.dossierinfo.Rechnungsnummern });
            this.dossierDatasource.push({ Key: 'Dossieransicht.Status', Value: this.data.dossierinfo.Status });
            this.dossierDatasource.push({ Key: 'Dossieransicht.Dienstleistungen', Value: this.data.dossierinfo.Dienstleistungen });

            this.data.dossierinfo.eroeffnungsdatum = this.parseDateString(this.data.dossierinfo.StartDatum);

            this.data.dossierinfo.Auftraege.forEach(element => {
                element.Grundforderung = element.Betrag - element.Teilzahlung;
            });

            // lieber als copy anlegen...
            this.direktzahlung = {};
            this.direktzahlung.Forderungen = [];
            this.data.dossierinfo.Auftraege.forEach((element) => {
                if (element.Restbetrag > 0)
                    this.direktzahlung.Forderungen.push(JSON.parse(JSON.stringify(element))
                    )
            });
            this.direktzahlung.Forderungen.forEach((element) => {
                element.Betrag = null;
            });

            this.direktzahlung.Dossierid = this.data.dossierinfo.Id;

            this.showdossierabschliessenBtn = this.data.dossierinfo.DossierType !== 2;
            this.showstundungBtn = this.data.dossierinfo.DossierType !== 2 && !this.data.dossierinfo.IsStartDossier;
            this.showrechnungzurueckziehenBtn = this.data.dossierinfo.DossierType !== 2 && this.data.dossierinfo.IsRechtlich === false;
            this.updateStundungFaelligkeit(0);

            this.betreibungsrueckzug = this.data.dossierinfo.IsRechtlich;
        }
    }

    /**
     * Parst einen Datums-String im Format DD.MM.YYYY zu einem Date-Objekt.
     */
    private parseDateString(value: string): Date | null {
        if (!value) return null;
        const parts = value.trim().split('.');
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
        return null;
    }

    public async melden(): Promise<void> {
        if (this.isUpdating || this.oroservice.isEmptyObject(this.direktzahlung)) {
            return;
        }

        this.isUpdating = true;

        const object = await this.oroservice.setDirektzahlung(this.direktzahlung);

        if (object.status === 500 || object.status === 200) {
            this.allfalse();
        }

        this.isUpdating = false;
        await this.refreshCase();
    }

    stundung() {
        this.showstundung=  false;
        if(this.isUpdating || this.oroservice.isEmptyObject(this.data.dossierinfo)) {
            return;
        }

        this.isUpdating = true;

        this.oroservice.setStundung(this.data.dossierinfo.Id, this.monate).then((object)=> {
          if (object.status === 400 ){
            this.allfalse();
          }
          else if (object.status === 500 ){
            this.data.dossierinfo.Stundungssaldo += this.monate * 30;
          }

          this.isUpdating = false;
        });
    }

    adresseRechnungsempfaengersenden() {
        if(this.isUpdating || this.oroservice.isEmptyObject(this.data.dossierinfo)) {
            return;
        }

        if (!this.canSubmitAdresseRechnungsempfaenger()) {
            return;
        }

        this.isUpdating = true;

        this.oroservice.setNeuerRechnungsempfaenger(this.data.dossierinfo.StandortRE).then(() => {
            this.allfalse();
            this.isUpdating = false;
        });
    }

    public async refreshCase(): Promise<void> {
        const loadedCase = await this.caseService.getCase(this.data.dossierinfo.Id);

        if (loadedCase != null) {
            this.data.dossierinfo = loadedCase;
            this.ngOnInit();
        } else {
            // TODO
        }
    }

    updateAuftragsbetraege() {
        this.data.dossierinfo.Auftraege.forEach(auftrag => {
            if (auftrag.Betrag == null)
            {
                auftrag.Betrag = 0;
            }
            if (auftrag.Restbetrag == null)
            {
                auftrag.Restbetrag = 0;
            }
            if (auftrag.Teilzahlung == null)
            {
                auftrag.Teilzahlung = 0;
            }
        });
    }

    public async rechnungzurueckziehensenden(): Promise<void> {
        if (this.isUpdating || this.oroservice.isEmptyObject(this.data.dossierinfo)) {
            return;
        }

        this.isUpdating = true;
        this.updateAuftragsbetraege();

        const object = await this.oroservice.setRechnungZurueckziehen(this.data.dossierinfo.Auftraege);

        if (object.status === 400) {
            this.allfalse();
        }

        this.isUpdating = false;
        await this.refreshCase();
        this.showrechnungzurueckziehen = false;
    }

  public async withdrawLossCertificates(): Promise<void> {
    if (this.isUpdating || this.oroservice.isEmptyObject(this.data.dossierinfo)) {
      return;
    }

    this.isUpdating = true;
    try {
      if (await this.caseService.withdrawLossCertificates([this.data.dossierinfo])) {
        await this.refreshCase();
        this.data.refreshDossiers?.();
      }

    } catch (error: any) {
      if (error?.status === 400) {
        this.allfalse();
      }
    } finally {
      this.isUpdating = false;
    }
  }

    public async btnabschliesen(): Promise<void> {
        if (this.data.dossierinfo.IsRechtlich) {
            this.allfalse();
            this.showdossierabschliessen = true;
            setTimeout(() => this.scrollToBottom(), 100);
        }
        else {
            await this.openDialogAbschliessen();
        }
    }

    btndirektzahlungmelden() {
        this.allfalse();
        this.showdirektzahlung = true;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    btnstundungmelden() {
        this.allfalse();
        this.showstundung = true;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    btnadresseRechnungsempfaengermelden() {
        this.allfalse();
        this.showadresseRechnungsempfaenger = true;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    btnrechnungzurueckziehen() {
        this.allfalse();
        this.showrechnungzurueckziehen = true;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    btnreclamation() {
        this.allfalse();
        this.showreclamation = true;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    private scrollToBottom() : void {
        this.scrollContent.nativeElement.scrollTop = this.scrollContent.nativeElement.scrollHeight;
    }

    allfalse() {
        this.showdirektzahlung = false;
        this.showstundung = false;
        this.showrechnungzurueckziehen = false;
        this.showadresseRechnungsempfaenger = false;
        this.showdossierabschliessen = false;
        this.showreclamation = false;
    }

    direktZahlungAdd(ford: any, index: number) {
      const forderungAdd: any = {
        Referenz: ford.Referenz,
        Id: ford.Id
      };

      this.direktzahlung.Forderungen.splice(index + 1, 0, forderungAdd);
    }

    checkDirektzahlungsDatumZukunft(datum: Date) {
        if (!datum) {
            return false;
        }
        return new Date().setHours(0, 0, 0, 0) < datum.setHours(0, 0, 0, 0);
    }

    checkDirektzahlungsVorEroefffnung(ford: any): boolean {
        if (!ford.Zahldatum || !ford.Betrag || ford.Zahldatum === "") {
            return false;
        }

        if (ford.Zahldatum < this.data.dossierinfo.eroeffnungsdatum) {
            //var fordOrigin = this.dossierinfo.Auftraege.find(o => o.Referenz == ford.Referenz);
            if (ford.Betrag > ford.Grundforderung) {
                return true;
            }
        }

        return false;
    }

    direktZahlungRemove(index: number) {
        this.direktzahlung.Forderungen.splice(index, 1);
    }

    public async openDialogAbschliessen(): Promise<void> {
        let content = "Dossieransicht.Abschließennachfrage";
        let rueckzug = false;

        if (this.data.dossierinfo.IsRechtlich) {
            content = "Dossieransicht.AbschließenTextBetreibungOhneRückzug";
            if (this.betreibungsrueckzug && this.isFullStorno()) {
                rueckzug = true;
                content = "Dossieransicht.AbschließenTextBetreibungMitRückzug";
            }
        }

        const result = await this.dialog.showDialog("Dossieransicht.Abschließen", content, "Ja", "Nein", DialogType.Question);
        if (result) {
            await this.oroservice.setabschliessen(this.data.dossierinfo.Id, rueckzug);
            this.allfalse();
        }
    }

    public async openDialogZurueckziehen(): Promise<void> {
        let content = "Dossieransicht.ZurueckziehenText";

        if (this.data.dossierinfo.IsRechtlich) {
            content = "Dossieransicht.ZurueckziehenTextBetreibung";
            if (this.isFullStorno()) {
                if (this.betreibungsrueckzug && this.isFullStorno()) {
                    this.data.dossierinfo.Auftraege.forEach((element) => {
                        element.DebtEnforcementWithdrawal = true;
                    });

                    content = "Dossieransicht.ZurueckziehenTextBetreibungMitRückzug";
                } else {
                    content = "Dossieransicht.ZurueckziehenTextBetreibungOhneRückzug";
                }
            }
        }

        const result = await this.dialog.showDialog("Dossieransicht.ZurueckziehenTitle", content, "Ja", "Nein", DialogType.Question);
        if (result) {
            await this.rechnungzurueckziehensenden();
            await this.dialog.showDialog("Meldung", "Dossieransicht.ZurueckziehenErgebnis", "Ok", "", DialogType.Info);
        }
    }

    canSubmitDirektzahlung(): boolean {
        let retVal = true;
        this.direktzahlung.Forderungen.forEach(element => {
            if (!element.Zahldatum || element.Betrag == null || element.Betrag < 0
                || this.checkDirektzahlungsDatumZukunft(element.Zahldatum)
                || this.checkDirektzahlungsVorEroefffnung(element)) {
                retVal = false;
            }
        });
        return retVal;
    }

    canSubmitRechnungzurueckziehen(): boolean {
        const results = this.data.dossierinfo.Auftraege.filter(o => o.Selected === true);
        return results && results.length > 0;
    }

    public isFullStorno(): boolean {
        const results = this.data.dossierinfo.Auftraege.filter(o => o.Selected === true);
        const retVal = results && results.length === this.data.dossierinfo.Auftraege.length;
        if (!retVal) {
            this.betreibungsrueckzug = false;
        }

        return retVal;
    }

    private updateStundungFaelligkeit(monate: number) : void {
        let date = this.parseDateString(this.data.dossierinfo.NextFaelligkeit);
        if (date) {
            date = new Date(date.setMonth(date.getMonth() + monate));
            const datepipe: DatePipe = new DatePipe('de-CH');
            this.naechstesDatum = datepipe.transform(date, 'dd.MM.yyyy');
        }
    }

    public onChangeStundung(): void {
        this.updateStundungFaelligkeit(this.monate);
    }

    public async sendReclamation(): Promise<void> {
        this.isSendingReclamation = true;

        try {
            await this.caseService.sendReclamation(this.reclamationBody, this.data.dossierinfo.Id);
            this.oroservice.openDialogMessage("Dossier.ReclamationFeedback");
            this.reclamationBody = "";
            this.allfalse();
        } finally {
            this.isSendingReclamation = false;
        }
    }

    public canSendReclamation() : boolean {
        return !this.isSendingReclamation && this.reclamationBody?.length > 5;
    }

    public canSubmitAdresseRechnungsempfaenger(): boolean {
        const standort = this.data.dossierinfo?.StandortRE;
        if (!standort) {
            return false;
        }

        const land = Number(standort.Land ?? 0);
        const plz = `${standort.Plz ?? ''}`.trim();
        const ort = `${standort.Ort ?? ''}`.trim();
        const strasse = `${standort.Anschrift ?? ''}`.trim();

        const plzGueltig = land === 0 ? /^\d{4}$/.test(plz) : plz.length > 0;

        return plzGueltig && ort.length > 0 && strasse.length > 0;
    }

    public getStatusKey(n: number) : string {
        let status = '?';
        switch (n) {
            case 1:
                status = 'DossierStatus.NichtGestartet';
                break;

            case 2:
                status = 'DossierStatus.VorrechtlichInkasso';
                break;

            case 3:
                status = 'DossierStatus.RechtlichesInkasso';
                break;

            case 4:
                status = 'DossierStatus.Ratenplan';
                break;

            case 5:
                status = 'DossierStatus.Abgerechnet';
                break;
        }

        return status;
    }

    public async downloadInvoice(): Promise<void> {
        const fileName = this.translate.instant('Dokumente.Abrechnung') + '.pdf';

        await this.oroservice.downloadDocument(
          this.data.dossierinfo.AbrechnungsDokument,
          fileName
        );
      }
}
